import {useFlux, useFluxListener} from '@nlabs/arkhamjs-utils-react';
import {Button} from '@nlabs/gothamui/components';
import {Moon, Sun} from '@nlabs/gothamui/icons';
import {createContext, useCallback, useContext, useLayoutEffect, useMemo, useState} from 'react';

import type {FluxFramework} from '@nlabs/arkhamjs';
import type {ReactElement, ReactNode} from 'react';

export type ReaktorTheme = 'dark' | 'light';

export const THEME_EVENTS = {CHANGED: 'REAKTOR_THEME_CHANGED'} as const;

const THEME_STATE_PATH = 'ui.theme';
const THEME_STORAGE_KEY = 'reaktor.theme';

type ThemeEvent = Readonly<{
  readonly theme?: ReaktorTheme;
}>;

type ThemeContextValue = Readonly<{
  readonly setTheme: (theme: ReaktorTheme) => void;
  readonly theme: ReaktorTheme;
}>;

type ThemeProviderProps = Readonly<{
  readonly children: ReactNode;
}>;

const ThemeContext = createContext<ThemeContextValue>({setTheme: () => undefined, theme: 'light'});

const isTheme = (value: unknown): value is ReaktorTheme => value === 'dark' || value === 'light';

export const getPreferredTheme = (): ReaktorTheme => {
  if(typeof globalThis.localStorage !== 'undefined') {
    try {
      const storedTheme = globalThis.localStorage.getItem(THEME_STORAGE_KEY);
      if(isTheme(storedTheme)) return storedTheme;
    } catch {
      // Browser privacy settings can make local storage unavailable.
    }
  }
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const applyTheme = (theme: ReaktorTheme): void => {
  if(typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0c0928' : '#f8f9ff');
};

export const createThemeActions = (flux: FluxFramework) => ({
  set: async (theme: ReaktorTheme): Promise<void> => {
    applyTheme(theme);
    try {
      globalThis.localStorage?.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // The in-memory Flux state still keeps the current preference available.
    }
    await flux.setState(THEME_STATE_PATH, theme);
    await flux.dispatch({theme, type: THEME_EVENTS.CHANGED});
  }
});

export const ThemeProvider = ({children}: ThemeProviderProps): ReactElement => {
  const flux = useFlux();
  const [theme, setThemeState] = useState<ReaktorTheme>(getPreferredTheme);
  const actions = useMemo(() => createThemeActions(flux), [flux]);

  useFluxListener(THEME_EVENTS.CHANGED, useCallback((event?: ThemeEvent) => {
    if(isTheme(event?.theme)) setThemeState(event.theme);
  }, []));

  useLayoutEffect(() => {
    applyTheme(theme);
    void flux.setState(THEME_STATE_PATH, theme);
  }, [flux, theme]);

  const setTheme = useCallback((nextTheme: ReaktorTheme) => {
    void actions.set(nextTheme);
  }, [actions]);

  const value = useMemo(() => ({setTheme, theme}), [setTheme, theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export type ThemeToggleProps = Readonly<{
  readonly className?: string;
}>;

export const ThemeToggle = ({className = ''}: ThemeToggleProps): ReactElement => {
  const {setTheme, theme} = useContext(ThemeContext);
  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  return <Button
    aria-label={`Switch to ${nextTheme} mode`}
    className={`size-9 shrink-0 rounded-full border-0 bg-white p-0 text-reaktor-ink hover:bg-reaktor-soft dark:bg-background-primary-dark ${className}`}
    icon={theme === 'dark' ? <Sun aria-hidden="true" className="size-4" /> : <Moon aria-hidden="true" className="size-4" />}
    onClick={() => setTheme(nextTheme)}
    title={`Switch to ${nextTheme} mode`}
    variant="text"
  />;
};
